import * as THREE from 'three';
import { windScale } from './conditions.js';
import { PUSHERS, pushers } from './trample.js';

/**
 * Flat-shaded instanced material that bends in the wind. `base` and `span` map the geometry's local Y range
 * onto 0 (rooted) .. 1 (free tip); `strength` is the tip's sway in world metres. The bend is applied after the
 * instance transform, so every blade leans the same way: rolling gust fronts sweep inland from the sea and
 * a faster flutter rides on top. Roots are shaded darker than tips so swaying fields shimmer.
 * One shared `time` drives every wind material.
 *
 * `trample` (left out for trees) lets the material be walked through: whatever stands near one of the shared `pushers` bends away from it
 * and springs back as the pusher's strength fades (see `TrampleTrail`). The plant bows from its root along an arc, so stems keep their
 * length and their foot stays put; see `Trample`.
 */
export function createWindMaterial(strength: number, base = .5, span = 1, rootShade = .8, tipShade = 1.12, trample?: Trample) {
  const time = { value: 0 };
  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true });
  const push = trample && { lean: trample.lean, root: trample.root ?? -base, bow: trample.bow ?? span, whole: trample.whole ?? false };
  material.customProgramCacheKey = () => `coast-wind-${strength}-${base}-${span}-${rootShade}-${tipShade}-${push ? Object.values(push).join('-') : 0}`;
  material.onBeforeCompile = shader => {
    shader.uniforms.windTime = time; shader.uniforms.windScale = windScale; shader.uniforms.uPush = pushers;
    shader.vertexShader = `uniform float windTime; uniform float windScale; uniform vec4 uPush[${PUSHERS}];\nvarying float vWindTip;\n` + shader.vertexShader.replace('#include <project_vertex>', `
      vec4 mvPosition = vec4(transformed, 1.0);
      vWindTip = clamp((position.y + ${base.toFixed(3)}) / ${span.toFixed(3)}, 0., 1.);
      #ifdef USE_INSTANCING
        mvPosition = instanceMatrix * mvPosition;
        vec2 windAt = mvPosition.xz;
        // A gust front travels north-east (away from the sea); flutter varies blade to blade.
        float front = sin(dot(windAt, vec2(.055, .032)) - windTime * 1.7);
        float gust = .45 + .55 * front * front + .22 * sin(windTime * 3.4 + windAt.x * .37 + windAt.y * .29);
        float bend = vWindTip * vWindTip * gust * windScale * ${strength.toFixed(3)};
        mvPosition.x += bend; mvPosition.z -= bend * .55; mvPosition.y -= bend * bend * .25;
        ${push ? `
        // Pushers are felt at each vertex (a clump parts around a foot) or at the plant's root (a flower or a bush leans as one piece).
        vec2 feltAt = ${push.whole ? 'instanceMatrix[3].xz' : 'windAt'}, shove = vec2(0.);
        for (int i = 0; i < ${PUSHERS}; i++) {
          vec4 pusher = uPush[i];
          if (pusher.w <= 0.) continue;
          vec2 away = feltAt - pusher.xy;
          if (dot(away, away) >= pusher.z * pusher.z) continue;
          float reach = length(away) + .001;
          shove += away / reach * pusher.w * smoothstep(pusher.z, 0., reach);
        }
        float pushed = length(shove);
        if (pushed > .001) {
          // Bow along an arc from the root: up to the bow height the stem curves evenly, above that it carries the full lean.
          // Each point sits where an arc of its own height ending at its own angle puts it, so the stem keeps its length.
          float stemScale = length(instanceMatrix[1].xyz), rise = max(position.y - ${push.root.toFixed(3)}, 0.) * stemScale;
          float lean = ${push.lean.toFixed(3)} * min(pushed, 1.) * min(rise / (${push.bow.toFixed(3)} * stemScale), 1.) + .0001;
          mvPosition.xz += shove / pushed * rise * (1. - cos(lean)) / lean; mvPosition.y += rise * (sin(lean) / lean - 1.);
        }` : ''}
      #endif
      mvPosition = modelViewMatrix * mvPosition;
      gl_Position = projectionMatrix * mvPosition;`);
    shader.fragmentShader = 'varying float vWindTip;\n' + shader.fragmentShader.replace('#include <color_fragment>',
      `#include <color_fragment>\n      diffuseColor.rgb *= mix(${rootShade.toFixed(3)}, ${tipShade.toFixed(3)}, vWindTip);`);
  };
  return { material, update: (seconds: number) => { time.value = seconds; } };
}

/**
 * How a wind material gives way to walkers. `lean` is the tip's lean in radians at full push; `root` is the geometry's local Y at the
 * plant's foot (default: the wind's root, `-base`); `bow` is the local height over which the stem curves (default: the wind's `span`);
 * `whole` feels the push once at the plant's root, so a flower or a bush leans as one piece instead of parting around the walker.
 */
export interface Trample { lean: number; root?: number; bow?: number; whole?: boolean }

/** Height above a tree's foot at which `createTreeWind`'s strength is the sway; taller trees lean further. */
const TREE_SWAY_HEIGHT = 8;
/** Foliage billow, as a share of the lean: crowns flex visibly while trunks only lean. */
const CANOPY_BILLOW = .8;

export interface TreeWind {
  /** The flat-shaded crown material for parts whose geometry has the tree's foot at local Y `foot`. */
  material(foot: number): THREE.Material;
  /** A swaying copy of `material` (a trunk's bark), for parts whose geometry has the tree's foot at local Y `foot`. */
  sway(material: THREE.Material, foot: number): THREE.Material;
  update(seconds: number): void;
}

/**
 * Wind for trees. Every part of a tree (trunk, stacked crown tiers) bends from the tree's foot along one curve, so the parts sway
 * as one piece and stay joined; the gust is sampled once per part at its origin, not per vertex. The lean grows with the cube of
 * the height, so the trunk stays stiff while the crown swings, and crowns also billow (`CANOPY_BILLOW`). `strength` is the sway in world
 * metres at `TREE_SWAY_HEIGHT` above the foot. Each part names `foot`, its geometry's local Y at the tree's foot: the instance's
 * Y scale turns local height into metres. Trees are never trampled. Shares the gust fronts of `createWindMaterial`.
 */
export function createTreeWind(strength: number): TreeWind {
  const time = { value: 0 }, crowns = new Map<string, THREE.Material>(), barks = new Map<THREE.Material, Map<string, THREE.Material>>();
  const bend = (shader: THREE.WebGLProgramParametersWithUniforms, foot: string, canopy = false) => {
    shader.uniforms.windTime = time; shader.uniforms.windScale = windScale;
    shader.vertexShader = 'uniform float windTime; uniform float windScale;\n' + shader.vertexShader.replace('#include <project_vertex>', `
      vec4 mvPosition = vec4(transformed, 1.0);
      #ifdef USE_INSTANCING
        float treeLean = max(transformed.y - ${foot}, 0.) * length(instanceMatrix[1].xyz) / ${TREE_SWAY_HEIGHT.toFixed(1)};
        mvPosition = instanceMatrix * mvPosition;
        vec2 windAt = instanceMatrix[3].xz;
        float front = sin(dot(windAt, vec2(.055, .032)) - windTime * 1.7);
        float gust = .45 + .55 * front * front + .22 * sin(windTime * 3.4 + windAt.x * .37 + windAt.y * .29);
        float bend = treeLean * treeLean * treeLean * gust * windScale * ${strength.toFixed(3)};
        ${canopy ? `
        // Foliage billows on top of the lean. It is a function of world position and time only, so tiers move together where they meet,
        // and it fades to nothing low on the tree, where the crown meets the trunk.
        vec3 leafAt = mvPosition.xyz;
        vec2 billow = vec2(sin(windTime * 4.3 + dot(leafAt, vec3(1.7, 1.3, 1.1))), sin(windTime * 3.1 + dot(leafAt, vec3(-1.2, 1.9, .8))));
        mvPosition.xz += billow * treeLean * treeLean * gust * windScale * ${(strength * CANOPY_BILLOW).toFixed(3)};` : ''}
        mvPosition.x += bend; mvPosition.z -= bend * .55; mvPosition.y -= bend * bend * .25;
      #endif
      mvPosition = modelViewMatrix * mvPosition;
      gl_Position = projectionMatrix * mvPosition;`);
  };
  return {
    material(foot) {
      const key = foot.toFixed(3);
      let crown = crowns.get(key);
      if (!crown) {
        const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true, vertexColors: true });
        material.customProgramCacheKey = () => `tree-wind-${strength}-${key}`; material.userData.treeFoot = foot;
        material.onBeforeCompile = shader => {
          bend(shader, key, true);
          // Each tier is shaded darker at its own base, so stacked tiers read as layers.
          shader.vertexShader = 'varying float vWindTip;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvWindTip = clamp(position.y + .5, 0., 1.);');
          shader.fragmentShader = 'varying float vWindTip;\n' + shader.fragmentShader.replace('#include <color_fragment>', '#include <color_fragment>\n      diffuseColor.rgb *= mix(.9, 1.05, vWindTip);');
        };
        crowns.set(key, crown = material);
      }
      return crown;
    },
    sway(material, foot) {
      const key = foot.toFixed(3), copies = barks.get(material) ?? new Map<string, THREE.Material>();
      barks.set(material, copies);
      let copy = copies.get(key);
      if (!copy) {
        copy = material.clone();
        const compile = material.onBeforeCompile.bind(material), cacheKey = material.customProgramCacheKey();
        copy.customProgramCacheKey = () => `${cacheKey}-tree-wind-${strength}-${key}`; copy.userData.treeFoot = foot;
        copy.onBeforeCompile = (shader, renderer) => { compile(shader, renderer); bend(shader, key); };
        copies.set(key, copy);
      }
      return copy;
    },
    update: seconds => { time.value = seconds; },
  };
}
